import {Box, Button, Stack, Typography} from "@mui/material";
import {IconArrowRight, IconBolt} from "@tabler/icons-react";
import {c, mono, softShadow, purpleGradient} from "../landingTokens";
import {Section, Eyebrow} from "./Shell";
import {ProductShot} from "./ProductShot";
import designerShot from "../../../assets/images/designer.png";

const PIPELINE = [
  {
    step: "01 · TELEPHONY",
    title: "Your PBX → SIP Connector",
    body: "Avaya — or whatever phone system you run — trunks into our SIP connector over standard SIP. Nothing on your side gets replaced.",
  },
  {
    step: "02 · AUDIO",
    title: "AudioSocket — live, one-to-one",
    body: "The connector streams raw call audio in both directions over a direct, one-to-one link — the reason the agent answers in under a second instead of after a beep.",
  },
  {
    step: "03 · LOGIC",
    title: "The flow you designed",
    body: "Audio hits the conversation flow built in the OpenBrain app. Prompts, branches, guardrails and escalation rules live there — editable without a release.",
  },
  {
    step: "04 · ACTIONS",
    title: "Your API, via MCP",
    body: "The flow calls your systems through MCP tools and REST — checking a balance, moving an appointment, opening a ticket while the caller is still on the line.",
  },
];

const JOURNEY = ["Your PBX", "SIP Connector", "AudioSocket — live, one-to-one", "Agent Live"];

export const Integrations = () => (
  <Section id="integrations" label="Integrations">
    <Box sx={{background: "linear-gradient(180deg, #FFFFFF 0%, #F4F1FF 100%)", borderRadius: "34px", boxShadow: softShadow, p: "clamp(32px, 5vw, 62px)"}}>
      <Eyebrow>Integrations</Eyebrow>
      <Typography component="h2" sx={{fontSize: "clamp(30px, 4.2vw, 50px)", lineHeight: 1.05, letterSpacing: "-0.03em", fontWeight: 600, maxWidth: 700, mb: 1.5, textWrap: "balance"}}>
        Four hops from your PBX to your API.
      </Typography>
      <Typography sx={{fontSize: 17, lineHeight: 1.5, color: c.inkSoft, maxWidth: 580, mb: 5.5}}>
        No rip-and-replace. Your PBX keeps doing its job — we sit next to it over SIP and reach your systems through a flow you can see and edit.
      </Typography>

      <Box className="reveal-stagger" sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)"}, gap: 1.75}}>
        {PIPELINE.map((s) => (
          <Box key={s.step} sx={{position: "relative", bgcolor: "rgba(124,108,246,0.05)", borderRadius: "20px", p: 3, pt: 3.5, overflow: "hidden"}}>
            {/* Short gradient accent — the shared device from the Metrics stats. */}
            <Box aria-hidden sx={{position: "absolute", top: 0, left: 24, width: 34, height: "3px", borderRadius: "0 0 3px 3px", background: purpleGradient}} />
            <Typography sx={{fontFamily: mono, fontSize: 11, color: c.purpleDeep, letterSpacing: "0.08em"}}>{s.step}</Typography>
            <Typography sx={{fontSize: 19, letterSpacing: "-0.015em", fontWeight: 600, mt: 1.75, mb: 1}}>{s.title}</Typography>
            <Typography sx={{fontSize: "14.5px", lineHeight: 1.5, color: c.inkSoft}}>{s.body}</Typography>
          </Box>
        ))}
      </Box>

      <Stack direction="row" flexWrap="wrap" useFlexGap alignItems="center" gap={1.25} sx={{mt: 1.75, bgcolor: "rgba(124,108,246,0.05)", borderRadius: "20px", p: 3}}>
        <Typography sx={{fontFamily: mono, fontSize: 11, color: c.inkMuted, letterSpacing: "0.08em", mr: 0.5}}>THE JOURNEY</Typography>
        {JOURNEY.map((step, i) => (
          <Box key={step} sx={{display: "flex", alignItems: "center", gap: 1.25}}>
            <Box
              sx={{
                fontSize: "12.5px",
                fontWeight: 500,
                bgcolor: c.white,
                color: c.purpleInk,
                px: 1.625,
                py: 0.75,
                borderRadius: "980px",
                boxShadow: "0 6px 16px -12px rgba(66,46,170,0.5)",
              }}
            >
              {step}
            </Box>
            {i < JOURNEY.length - 1 && <IconArrowRight size={14} stroke={2} color={c.inkMuted} />}
          </Box>
        ))}
      </Stack>

      {/* Speed callout — the single biggest objection (integration pain) answered in one line. */}
      <Stack
        direction={{xs: "column", sm: "row"}}
        alignItems={{xs: "flex-start", sm: "center"}}
        justifyContent="space-between"
        gap={2}
        sx={{mt: 1.75, background: purpleGradient, borderRadius: "20px", p: {xs: 2.5, md: 3}}}
      >
        <Stack direction="row" alignItems="center" gap={1.25}>
          <IconBolt size={20} color="#FFFFFF" style={{flexShrink: 0}} />
          <Typography sx={{fontSize: {xs: 14.5, md: 15.5}, fontWeight: 600, color: c.white, textWrap: "balance"}}>
            Connect your PBX through our SIP connector in a few minutes — and start your agent now.
          </Typography>
        </Stack>
        <Button
          href="#demo"
          endIcon={<IconArrowRight size={16} />}
          sx={{
            flexShrink: 0,
            borderRadius: "980px",
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            py: 1.25,
            bgcolor: c.white,
            color: c.purpleInk,
            "&:hover": {bgcolor: c.white, boxShadow: "0 14px 30px -16px rgba(20,21,26,0.45)", transform: "translateY(-2px)"},
          }}
        >
          Start Now
        </Button>
      </Stack>

      <Box sx={{mt: 1.75}}>
        <ProductShot src={designerShot} alt="OpenBrain flow designer canvas" ratio="21 / 8" />
      </Box>
    </Box>
  </Section>
);
